import { Fragment, useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Save, RotateCcw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

const ROLE_LABELS = {
  manager: 'Manager',
  operations_manager: 'Ops Manager',
  kitchen: 'Kitchen',
  fulfilment: 'Fulfilment',
  customer_support: 'Support',
  finance: 'Finance',
  driver: 'Driver',
  admin: 'Admin (legacy)',
};

export default function AdminPermissions() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [dirtyRoles, setDirtyRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const fetchMatrix = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/permissions`, { headers: authHeaders(), credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData(d);
      setMatrix(d.matrix || {});
      setDirtyRoles([]);
    } catch (e) {
      toast.error('Failed to load permission matrix');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const toggle = (role, perm) => {
    setMatrix(prev => {
      const current = prev[role] || [];
      const next = current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm];
      return { ...prev, [role]: next };
    });
    setDirtyRoles(prev => (prev.includes(role) ? prev : [...prev, role]));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const role of dirtyRoles) {
        const res = await fetch(`${API_URL}/api/admin/permissions`, {
          method: 'PUT', headers: authHeaders(), credentials: 'include',
          body: JSON.stringify({ role, permissions: matrix[role] || [] }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Failed to save ${role}`);
        }
      }
      toast.success('Permissions saved — changes apply immediately');
      fetchMatrix();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetRole = async (role) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/permissions/reset`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error('Reset failed');
      toast.success(`${ROLE_LABELS[role] || role} reset to defaults`);
      fetchMatrix();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">Loading permission matrix…</div>;
  }
  if (!data) {
    return <div className="py-12 text-center text-muted-foreground" data-testid="permissions-load-error">Could not load the permission matrix.</div>;
  }

  const roles = data.roles || [];
  const perms = data.permissions || [];
  const groups = [...new Set(perms.map(p => p.group))];

  return (
    <div className="space-y-6" data-testid="admin-permissions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-foreground flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-primary" />
            Staff Permissions
          </h1>
          <p className="text-muted-foreground mt-1">Control exactly what each BOH role can do. Changes apply to the API immediately.</p>
        </div>
        <Button onClick={saveAll} disabled={saving || dirtyRoles.length === 0} className="gap-2" data-testid="permissions-save-btn">
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : dirtyRoles.length > 0 ? `Save changes (${dirtyRoles.length} role${dirtyRoles.length > 1 ? 's' : ''})` : 'Save changes'}
        </Button>
      </div>

      <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>Super Admin always has every permission and can't be edited. A role marked <Badge variant="outline" className="mx-1">custom</Badge> has been changed from its defaults — use the reset arrow to restore them.</div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left font-semibold p-3 sticky left-0 bg-muted/40 min-w-[220px]">Permission</th>
                {roles.map(role => (
                  <th key={role} className="p-3 text-center font-semibold whitespace-nowrap" data-testid={`permissions-role-header-${role}`}>
                    <div className="flex flex-col items-center gap-1">
                      <span>{ROLE_LABELS[role] || role}</span>
                      <div className="flex items-center gap-1">
                        {(data.overridden_roles || []).includes(role) && <Badge variant="outline" className="text-[10px]">custom</Badge>}
                        <button
                          onClick={() => resetRole(role)}
                          title="Reset role to defaults"
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`permissions-reset-${role}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <Fragment key={group}>
                  <tr className="bg-muted/20">
                    <td colSpan={roles.length + 1} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground sticky left-0">{group}</td>
                  </tr>
                  {perms.filter(p => p.group === group).map(perm => (
                    <tr key={perm.key} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="p-3 sticky left-0 bg-background">
                        <p className="font-medium">{perm.label}</p>
                        <p className="text-xs text-muted-foreground font-mono">{perm.key}</p>
                      </td>
                      {roles.map(role => (
                        <td key={`${role}-${perm.key}`} className="p-2 text-center">
                          <Switch
                            checked={(matrix[role] || []).includes(perm.key)}
                            onCheckedChange={() => toggle(role, perm.key)}
                            data-testid={`perm-switch-${role}-${perm.key}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
